/**
 * RevenueRegisterManualEntryController — modale de saisie d'une recette
 * comptoir (EPIC-016 / 16.4, spec §7.H.B + §7.H.D).
 *
 * Pilote la modale `<section id="manual-entry">` :
 *   - Action `open` : ouvre la modale en mode création (reset complet).
 *   - Action `openForCorrection` : ouvre la modale en mode contre-écriture
 *     depuis un bouton de ligne du tableau (lit
 *     `data-revenue-register-manual-entry-correction-source-id-value` et
 *     `data-revenue-register-manual-entry-correction-source-entry-number-value`
 *     sur le bouton — câblage du bouton à charge de 16.3/16.7).
 *   - Action `close` : ferme la modale + reset.
 *   - Action `submit` : POST /revenue/manual-entries (création) ou
 *     POST /revenue/manual-entries/{id}/correction (contre-écriture).
 *   - Action `onAmountHtChange` / `onVatRateChange` / `onAmountTtcChange` :
 *     recalcul automatique des montants liés (mode franchise V1 : TTC seul
 *     est saisi, HT = TTC, TVA = 0 ; mode assujetti pré-câblé pour le
 *     basculement EPIC-019).
 *
 * Validation côté client :
 *   - `entryDate ≤ aujourd'hui` (vérif HTML5 `max` + check JS au submit).
 *   - `amountInclVat > 0` et `< 76 €` strict (seuil §7.H.D — sauf mode
 *     correction où la limite ne s'applique pas).
 *   - Champs obligatoires : date, description, activité, TTC, mode de
 *     règlement.
 *   - Le serveur reste l'autorité — toute erreur 422/409 est mappée vers
 *     un message FR affiché dans `errorBox` + un toast `error`.
 *
 * Au succès :
 *   - Dispatche `revenue-register:entry-created` (bubbles) → écouté par
 *     `revenue-register` (44.6) pour rafraîchir le tableau.
 *   - Toast `success` + fermeture modale.
 *
 * V1 — `isVatFranchise()` (stub backend) retourne toujours `true` →
 * `VAT_FRANCHISE_DEFAULT = true` ici, qui force le lock du select TVA à
 * `0` et synchronise HT = TTC. À basculer dynamiquement en EPIC-019.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const VAT_FRANCHISE_DEFAULT = true;
const AMOUNT_THRESHOLD = 76;

const ERROR_MESSAGES = {
  not_bic_vente_eligible:
    "Vous n'avez aucune activité de vente de marchandises (BIC vente) active. La saisie manuelle est réservée à ce régime.",
  activity_required: "L'activité concernée est requise.",
  activity_invalid:
    "L'activité sélectionnée n'est pas une activité de vente de marchandises active.",
  entry_date_required: "La date d'encaissement est requise.",
  entry_date_invalid: "La date d'encaissement est invalide.",
  entry_date_in_future: "La date d'encaissement ne peut pas être postérieure à aujourd'hui.",
  entry_date_in_closed_year:
    "L'exercice est clôturé au livre des recettes — vous ne pouvez plus saisir d'entrée à cette date.",
  description_required: "La nature de la recette est requise.",
  payment_method_required: "Le mode de règlement est requis.",
  payment_method_invalid: "Mode de règlement invalide.",
  amountInclVat_required: "Le montant TTC est requis.",
  amountInclVat_invalid: "Le montant TTC doit être un nombre.",
  amount_below_zero: "Le montant TTC doit être strictement positif.",
  amount_above_threshold:
    "Une vente sans facture est limitée à 76 € TTC. Au-delà, émettez une facture nominative.",
  amounts_inconsistent:
    "Incohérence des montants : HT + TVA doit être égal à TTC (±0,01 €).",
  correction_of_correction: "Impossible de corriger une écriture corrective.",
  correction_already_exists: "Une écriture corrective existe déjà pour cette entrée.",
  source_not_found: "Entrée source introuvable.",
  validation_failed: "Certains champs sont invalides. Vérifiez votre saisie.",
  business_rule_violation: "Cette action n'est pas autorisée.",
  conflict: "Cette action est en conflit avec l'état actuel.",
  unauthorized: "Vous n'êtes pas autorisé à effectuer cette action.",
};

export default class RevenueRegisterManualEntryController extends BaseController {
  static targets = [
    "form",
    "dateInput",
    "descriptionInput",
    "activitySelect",
    "amountHtInput",
    "vatRateSelect",
    "vatAmountInput",
    "amountTtcInput",
    "paymentMethodSelect",
    "pieceReferenceInput",
    "noteInput",
    "attachmentInput",
    "submitButton",
    "errorBox",
    "errorTitle",
    "errorMessage",
    "correctionBanner",
    "correctionSourceLabel",
  ];

  static values = {
    correctionSourceId: String,
    correctionSourceEntryNumber: Number,
  };

  _correctionMode = false;
  _correctionSourceId = null;
  _correctionSourceEntryNumber = null;
  _vatFranchise = VAT_FRANCHISE_DEFAULT;
  _submitting = false;
  _activitiesLoaded = false;

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  open() {
    this._enterCreationMode();
    this._showModal();
  }

  openForCorrection(event) {
    const trigger = event?.currentTarget;
    const sourceId = trigger?.dataset?.revenueRegisterManualEntryCorrectionSourceIdValue
      ?? this.correctionSourceIdValue;
    const sourceNumber =
      trigger?.dataset?.revenueRegisterManualEntryCorrectionSourceEntryNumberValue
      ?? this.correctionSourceEntryNumberValue;

    if (!sourceId) {
      this.showError("Impossible d'ouvrir l'écriture corrective (entrée source manquante).");
      return;
    }

    this._enterCorrectionMode(sourceId, sourceNumber);
    this._showModal();
  }

  close() {
    this._hideModal();
    this._resetState();
  }

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  async submit(event) {
    if (event) event.preventDefault();
    if (this._submitting) return;

    this._clearError();

    const validationError = this._validateClient();
    if (validationError) {
      this._showError(validationError);
      return;
    }

    this._submitting = true;
    this._setSubmitButtonState(true);

    try {
      const entry = this._correctionMode
        ? await api.createRevenueManualEntryCorrection(
            this._correctionSourceId,
            this._readDescriptionForCorrection(),
          )
        : await api.createRevenueManualEntry(
            this._buildPayload(),
            this._readAttachment(),
          );

      this._dispatchSavedEvent(entry);
      this.showSuccess(
        this._correctionMode
          ? "Écriture corrective scellée."
          : "Recette scellée.",
      );
      this.close();
    } catch (error) {
      this._handleApiError(error);
    } finally {
      this._submitting = false;
      this._setSubmitButtonState(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Recalcul automatique des montants liés
  // ---------------------------------------------------------------------------

  onAmountHtChange() {
    if (this._vatFranchise) {
      this._mirrorHtToTtc();
      return;
    }
    this._recomputeFromHt();
  }

  onVatRateChange() {
    if (this._vatFranchise) {
      this._lockVatToZero();
      return;
    }
    this._recomputeFromHt();
  }

  onAmountTtcChange() {
    if (this._vatFranchise) {
      this._mirrorTtcToHt();
      return;
    }
    this._recomputeFromTtc();
  }

  // ---------------------------------------------------------------------------
  // Mode helpers
  // ---------------------------------------------------------------------------

  _enterCreationMode() {
    this._correctionMode = false;
    this._correctionSourceId = null;
    this._correctionSourceEntryNumber = null;
    this._resetForm();
    if (this.hasCorrectionBannerTarget) {
      this.correctionBannerTarget.hidden = true;
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.textContent = "Sceller la recette";
    }
    this._ensureActivitiesLoaded();
  }

  _enterCorrectionMode(sourceId, sourceEntryNumber) {
    this._correctionMode = true;
    this._correctionSourceId = sourceId;
    this._correctionSourceEntryNumber = sourceEntryNumber;
    this._resetForm();
    if (this.hasCorrectionBannerTarget) {
      this.correctionBannerTarget.hidden = false;
    }
    if (this.hasCorrectionSourceLabelTarget) {
      const label = sourceEntryNumber
        ? `#${String(sourceEntryNumber).padStart(3, "0")}`
        : "#—";
      this.correctionSourceLabelTarget.textContent = label;
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.textContent = "Sceller la contre-écriture";
    }
  }

  _resetState() {
    this._correctionMode = false;
    this._correctionSourceId = null;
    this._correctionSourceEntryNumber = null;
    this._submitting = false;
    this._clearError();
  }

  _resetForm() {
    if (this.hasFormTarget) {
      this.formTarget.reset();
    }
    if (this.hasDateInputTarget) {
      const today = new Date().toISOString().slice(0, 10);
      this.dateInputTarget.value = today;
      this.dateInputTarget.max = today;
    }
    if (this._vatFranchise) {
      this._lockVatToZero();
    }
  }

  _showModal() {
    this.element.hidden = false;
    this.element.scrollIntoView({ behavior: "smooth", block: "start" });
    if (this.hasDateInputTarget) {
      this.dateInputTarget.focus();
    }
  }

  _hideModal() {
    this.element.hidden = true;
  }

  _setSubmitButtonState(loading) {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = loading;
    if (loading) {
      this.submitButtonTarget.dataset.originalLabel = this.submitButtonTarget.textContent;
      this.submitButtonTarget.textContent = "Scellement…";
    } else if (this.submitButtonTarget.dataset.originalLabel) {
      this.submitButtonTarget.textContent = this.submitButtonTarget.dataset.originalLabel;
      delete this.submitButtonTarget.dataset.originalLabel;
    }
  }

  // ---------------------------------------------------------------------------
  // Validation client + payload
  // ---------------------------------------------------------------------------

  _validateClient() {
    if (this._correctionMode) {
      return null;
    }
    const entryDate = this.hasDateInputTarget ? this.dateInputTarget.value : "";
    if (!entryDate) return ERROR_MESSAGES.entry_date_required;

    const today = new Date().toISOString().slice(0, 10);
    if (entryDate > today) return ERROR_MESSAGES.entry_date_in_future;

    const description = this.hasDescriptionInputTarget
      ? this.descriptionInputTarget.value.trim()
      : "";
    if (!description) return ERROR_MESSAGES.description_required;

    const activityId = this.hasActivitySelectTarget ? this.activitySelectTarget.value : "";
    if (!activityId) return ERROR_MESSAGES.activity_required;

    const paymentMethod = this.hasPaymentMethodSelectTarget
      ? this.paymentMethodSelectTarget.value
      : "";
    if (!paymentMethod) return ERROR_MESSAGES.payment_method_required;

    const ttc = this._parseAmount(
      this.hasAmountTtcInputTarget ? this.amountTtcInputTarget.value : "",
    );
    if (ttc === null) return ERROR_MESSAGES.amountInclVat_required;
    if (ttc <= 0) return ERROR_MESSAGES.amount_below_zero;
    if (ttc >= AMOUNT_THRESHOLD) return ERROR_MESSAGES.amount_above_threshold;

    return null;
  }

  _buildPayload() {
    const ttc = this._parseAmount(this.amountTtcInputTarget.value) ?? 0;
    const ht = this._vatFranchise
      ? ttc
      : (this._parseAmount(this.amountHtInputTarget?.value) ?? ttc);
    const vatAmount = this._vatFranchise
      ? 0
      : (this._parseAmount(this.vatAmountInputTarget?.value) ?? 0);
    const vatRate = this._vatFranchise
      ? 0
      : this._readVatRatePercent();

    return {
      entryDate: this.dateInputTarget.value,
      description: this.descriptionInputTarget.value.trim(),
      activityId: this.activitySelectTarget.value,
      amountInclVat: this._formatAmount(ttc),
      amountExclVat: this._formatAmount(ht),
      vatAmount: this._formatAmount(vatAmount),
      vatRate: this._formatAmount(vatRate),
      paymentMethod: this.paymentMethodSelectTarget.value,
      pieceReference: this._readOptional(this.pieceReferenceInputTarget?.value),
      note: this._readOptional(this.noteInputTarget?.value),
    };
  }

  _readDescriptionForCorrection() {
    const raw = this.hasDescriptionInputTarget
      ? this.descriptionInputTarget.value.trim()
      : "";
    return raw === "" ? null : raw;
  }

  _readAttachment() {
    if (!this.hasAttachmentInputTarget) return null;
    const files = this.attachmentInputTarget.files;
    if (!files || files.length === 0) return null;
    return files[0];
  }

  _readOptional(value) {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    return trimmed === "" ? undefined : trimmed;
  }

  // ---------------------------------------------------------------------------
  // Recalcul auto (franchise vs assujetti)
  // ---------------------------------------------------------------------------

  _lockVatToZero() {
    if (this.hasVatRateSelectTarget) {
      this.vatRateSelectTarget.value = "0";
    }
    if (this.hasVatAmountInputTarget) {
      this.vatAmountInputTarget.value = "0,00";
      this.vatAmountInputTarget.disabled = true;
    }
  }

  _mirrorHtToTtc() {
    const ht = this._parseAmount(this.amountHtInputTarget?.value);
    if (ht === null || !this.hasAmountTtcInputTarget) return;
    this.amountTtcInputTarget.value = this._formatAmountDisplay(ht);
  }

  _mirrorTtcToHt() {
    const ttc = this._parseAmount(this.amountTtcInputTarget?.value);
    if (ttc === null || !this.hasAmountHtInputTarget) return;
    this.amountHtInputTarget.value = this._formatAmountDisplay(ttc);
  }

  _recomputeFromHt() {
    const ht = this._parseAmount(this.amountHtInputTarget?.value);
    const ratePercent = this._readVatRatePercent();
    if (ht === null) return;
    const vat = ht * (ratePercent / 100);
    const ttc = ht + vat;
    if (this.hasVatAmountInputTarget) {
      this.vatAmountInputTarget.value = this._formatAmountDisplay(vat);
    }
    if (this.hasAmountTtcInputTarget) {
      this.amountTtcInputTarget.value = this._formatAmountDisplay(ttc);
    }
  }

  _recomputeFromTtc() {
    const ttc = this._parseAmount(this.amountTtcInputTarget?.value);
    const ratePercent = this._readVatRatePercent();
    if (ttc === null) return;
    const ht = ratePercent === 0 ? ttc : ttc / (1 + ratePercent / 100);
    const vat = ttc - ht;
    if (this.hasAmountHtInputTarget) {
      this.amountHtInputTarget.value = this._formatAmountDisplay(ht);
    }
    if (this.hasVatAmountInputTarget) {
      this.vatAmountInputTarget.value = this._formatAmountDisplay(vat);
    }
  }

  /**
   * Lit la valeur du select TVA en pourcentage entier (convention projet
   * cohérente avec `ArticleService` côté backend). Le select de la maquette
   * propose `value="0"`, `value="0.20"`, `value="0.10"`, `value="0.055"` —
   * on multiplie par 100 pour normaliser. L'option `value="autre"` n'est
   * pas câblée en V1 (renvoie 0).
   */
  _readVatRatePercent() {
    if (!this.hasVatRateSelectTarget) return 0;
    const raw = this.vatRateSelectTarget.value;
    if (raw === "autre" || raw === "" || raw == null) return 0;
    const parsed = this._parseAmount(raw);
    if (parsed === null) return 0;
    return parsed > 1 ? parsed : parsed * 100;
  }

  _parseAmount(value) {
    if (value === null || value === undefined) return null;
    const str = String(value).replace(/\s/g, "").replace(",", ".");
    if (str === "") return null;
    const num = Number.parseFloat(str);
    return Number.isFinite(num) ? num : null;
  }

  _formatAmount(value) {
    return Number.parseFloat(value).toFixed(2);
  }

  _formatAmountDisplay(value) {
    return Number.parseFloat(value).toFixed(2).replace(".", ",");
  }

  // ---------------------------------------------------------------------------
  // Activités BIC vente — peuplement dynamique du select
  // ---------------------------------------------------------------------------

  async _ensureActivitiesLoaded() {
    if (this._activitiesLoaded || !this.hasActivitySelectTarget) return;
    try {
      const response = await api.listActivities();
      const list = Array.isArray(response?.activities) ? response.activities : [];
      const bicVente = list.filter(
        (a) => a?.category === "bic_vente" && a?.status === "active",
      );
      if (bicVente.length === 0) return;

      this.activitySelectTarget.innerHTML = "";
      for (const activity of bicVente) {
        const opt = document.createElement("option");
        opt.value = activity.id;
        opt.textContent = activity.description ?? activity.nafCode ?? activity.id;
        this.activitySelectTarget.appendChild(opt);
      }
      if (bicVente.length === 1) {
        this.activitySelectTarget.value = bicVente[0].id;
      }
      this._activitiesLoaded = true;
    } catch {
      // Silencieux : le select garde sa valeur statique. Le backend valide.
    }
  }

  // ---------------------------------------------------------------------------
  // Error handling
  // ---------------------------------------------------------------------------

  _handleApiError(error) {
    if (error instanceof APIError) {
      const data = error.data ?? {};
      const code = data?.error?.code ?? "";
      const serverMessage = data?.error?.message ?? "";
      const mapped = ERROR_MESSAGES[code] ?? serverMessage ?? error.message;
      this._showError(mapped || "Erreur lors de la création.");
      return;
    }
    this._showError("Erreur réseau. Veuillez réessayer.");
  }

  _showError(message) {
    if (this.hasErrorBoxTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorBoxTarget.hidden = false;
      this.errorBoxTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    this.showError(message);
  }

  _clearError() {
    if (this.hasErrorBoxTarget) {
      this.errorBoxTarget.hidden = true;
    }
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = "—";
    }
  }

  _dispatchSavedEvent(entry) {
    this.element.dispatchEvent(
      new CustomEvent("revenue-register:entry-created", {
        bubbles: true,
        detail: { entry, correctionMode: this._correctionMode },
      }),
    );
  }
}
